import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useFormik, Form, FormikProvider } from "formik";
import { Input, Table, Modal, Button, Space, Switch, Tag, Select as SelectAnt } from "antd";
import { createPlatforms, updatePlatforms, getPlatformsList } from "../../appRedux/actions/platforms";
import { getTagsList } from "../../appRedux/actions/tags";
import { hideDialog as hideDialogCommon } from "../../appRedux/actions/common";

const { Option } = SelectAnt;

const INITIAL_STATE = {
  name: "",
  code: "",
  description: "",
  website: "",
  pricingModel: "",
  affiliateProgram: "",
  focusAreas: "",
  certificatesOffered: "",
  enrichmentStatus: "BASIC",
  tags: [],
  faqs: [],
};

function FormView(props) {
  const { platformInfo, queryInfo } = useSelector((state) => ({
    platformInfo: state.platforms.platformInfo,
    queryInfo: state.platforms.queryInfo,
  }));
  const tagsList = useSelector((state) => state.tags?.tagsList || []);

  const { isUpdate } = props;
  const [formInfo, setFormInfo] = useState(INITIAL_STATE);
  const dispatch = useDispatch();
  const [isFaqModalOpen, setIsFaqModalOpen] = useState(false);
  const [editingFaqIndex, setEditingFaqIndex] = useState(null);
  const [faqFormValues, setFaqFormValues] = useState({
    question: "",
    answer: "",
    priority: 0,
    isActive: true,
  });

  useEffect(() => {
    dispatch(getTagsList());
  }, [dispatch]);

  useEffect(() => {
    if (isUpdate && platformInfo && platformInfo._id) {
      const meta = platformInfo.extendedMetadata || {};
      setFormInfo({
        ...INITIAL_STATE,
        ...platformInfo,
        description: platformInfo.description || meta.description || "",
        website: meta.website || "",
        pricingModel: meta.pricingModel || "",
        affiliateProgram: meta.affiliateProgram || "",
        focusAreas: Array.isArray(meta.focusAreas) ? meta.focusAreas.join(", ") : (meta.focusAreas || ""),
        certificatesOffered: meta.certificatesOffered || "",
        enrichmentStatus: meta.enrichmentStatus || (platformInfo.description || meta.website ? "ENRICHED" : "BASIC"),
        tags: Array.isArray(platformInfo.tags)
          ? platformInfo.tags.map((t) => (typeof t === "string" ? t : t._id))
          : [],
        faqs: platformInfo.faqs || [],
      });
    } else {
      setFormInfo({
        ...INITIAL_STATE,
      });
    }
  }, [isUpdate, platformInfo]);

  const FormSchema = Yup.object().shape({
    name: Yup.string().required("Platform name is required."),
    code: Yup.string().required("Platform code is required."),
    description: Yup.string().optional(),
    website: Yup.string().url("Must be a valid URL (e.g. https://www.udemy.com)").optional().nullable(),
    pricingModel: Yup.string().optional(),
    affiliateProgram: Yup.string().optional(),
    focusAreas: Yup.string().optional(),
    certificatesOffered: Yup.string().optional(),
    enrichmentStatus: Yup.string().optional(),
    tags: Yup.array().optional(),
    faqs: Yup.array().of(
      Yup.object().shape({
        question: Yup.string().required("Question is required"),
        answer: Yup.string().required("Answer is required"),
        priority: Yup.number().optional(),
        isActive: Yup.boolean().optional(),
      })
    ),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: formInfo,
    validationSchema: FormSchema,
    onSubmit: (data) => {
      const focusArr = typeof data.focusAreas === "string" 
        ? data.focusAreas.split(",").map(s => s.trim()).filter(Boolean)
        : (data.focusAreas || []);

      const payload = {
        name: data.name,
        code: data.code,
        description: data.description || "",
        tags: data.tags || [],
        extendedMetadata: {
          website: data.website || "",
          pricingModel: data.pricingModel || "",
          affiliateProgram: data.affiliateProgram || "",
          focusAreas: focusArr,
          certificatesOffered: data.certificatesOffered || "",
          enrichmentStatus: data.enrichmentStatus || "BASIC",
        },
        faqs: (data.faqs || []).map(f => ({
          question: f.question,
          answer: f.answer,
          priority: Number(f.priority) || 0,
          isActive: f.isActive !== false,
        })),
      };

      if (isUpdate && platformInfo && platformInfo._id) {
        dispatch(
          updatePlatforms({
            ...payload,
            platformId: platformInfo._id,
          })
        ).then(() => {
          dispatch(getPlatformsList(queryInfo)).catch(() => {});
          dispatch(hideDialogCommon());
        }).catch(() => {});
      } else {
        dispatch(
          createPlatforms(payload)
        ).then(() => {
          dispatch(getPlatformsList(queryInfo)).catch(() => {});
          dispatch(hideDialogCommon());
        }).catch(() => {});
      }
    },
  });

  const {
    errors,
    touched,
    values,
    handleSubmit,
    getFieldProps,
    setFieldValue,
  } = formik;

  return (
    <FormikProvider value={formik}>
      <Form
        id="contactForm"
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
        className="signin-form"
      >
        <div className="row m-0 g-3">
          {/* Core Info */}
          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500 }}>
                Platform Name <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="text" placeholder="e.g. Coursera" {...getFieldProps("name")} />
              {touched.name && errors.name && <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px" }}>{errors.name}</p>}
            </div>
          </div>

          <div className="col-md-6">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500 }}>
                Platform Code <span className="text-danger">*</span>
              </label>
              <input className="form-control" type="text" placeholder="e.g. coursera" {...getFieldProps("code")} />
              {touched.code && errors.code && <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "12px" }}>{errors.code}</p>}
            </div>
          </div>

          <div className="col-md-12">
            <div className="form-group mb-3">
              <label className="col-form-label pb-1" style={{ fontWeight: 500 }}>
                Description & Overview
              </label>
              <textarea
                className="form-control"
                rows={2}
                placeholder="Brief summary of the platform, target audience, and course catalog..."
                {...getFieldProps("description")}
              />
            </div>
          </div>

          {/* Enriched Metadata Section */}
          <div className="col-md-12">
            <div className="card p-3 border-0 bg-light" style={{ borderRadius: "8px", border: "1px solid #e5e7eb" }}>
              <h6 className="mb-3" style={{ fontWeight: 600, color: "#374151" }}>
                <i className="fa fa-info-circle text-primary mr-1" /> Enriched Metadata & Affiliate Info
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Official Website URL</label>
                    <input className="form-control form-control-sm" type="text" placeholder="https://www.coursera.org" {...getFieldProps("website")} />
                    {touched.website && errors.website && <p className="error-msg text-danger mt-1 mb-0" style={{ fontSize: "11px" }}>{errors.website}</p>}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Enrichment Status</label>
                    <select className="form-control form-control-sm form-select" {...getFieldProps("enrichmentStatus")}>
                      <option value="BASIC">Basic Profile</option>
                      <option value="ENRICHED">Enriched Profile (100% Complete)</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Pricing & Subscription Model</label>
                    <input className="form-control form-control-sm" type="text" placeholder="e.g. Coursera Plus & Single Purchase" {...getFieldProps("pricingModel")} />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Affiliate Program / Network</label>
                    <input className="form-control form-control-sm" type="text" placeholder="e.g. Impact.com (10-45% Comm)" {...getFieldProps("affiliateProgram")} />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Focus Areas / Categories (comma separated)</label>
                    <input className="form-control form-control-sm" type="text" placeholder="e.g. Data Science, Business, Tech" {...getFieldProps("focusAreas")} />
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="form-group mb-2">
                    <label className="col-form-label-sm pb-1" style={{ fontWeight: 500 }}>Certificates & Credentials Offered</label>
                    <input className="form-control form-control-sm" type="text" placeholder="e.g. Professional Certs & Degrees" {...getFieldProps("certificatesOffered")} />
                  </div>
                </div>

                {/* Centralized Tag Management Integration */}
                <div className="col-md-12">
                  <div className="form-group mb-1">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <label className="col-form-label-sm pb-0" style={{ fontWeight: 600 }}>
                        <i className="la la-tags text-primary me-1" /> Platform Decision & Comparison Tags
                      </label>
                      <span className="text-muted" style={{ fontSize: "11px" }}>
                        Powers comparison axes & platform badge highlights
                      </span>
                    </div>
                    <SelectAnt
                      mode="multiple"
                      allowClear
                      style={{ width: "100%" }}
                      placeholder="Select platform tags (Pricing, Format, Strength, Audience, Perks...)"
                      value={values.tags || []}
                      onChange={(selectedIds) => setFieldValue("tags", selectedIds)}
                      optionFilterProp="children"
                      showSearch
                    >
                      {tagsList.map((tag) => (
                        <Option key={tag._id} value={tag._id}>
                          <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                            {tag.color && (
                              <span
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: tag.color,
                                  display: "inline-block",
                                }}
                              />
                            )}
                            {tag.icon && <i className={tag.icon} style={{ fontSize: "12px" }} />}
                            <span>{tag.name}</span>
                            {tag.group && (
                              <span
                                style={{
                                  fontSize: "10px",
                                  backgroundColor: "#f3f4f6",
                                  padding: "1px 6px",
                                  borderRadius: "4px",
                                  color: "#6b7280",
                                }}
                              >
                                {tag.group}
                              </span>
                            )}
                            <span style={{ fontSize: "9px", color: "#9ca3af" }}>({tag.scope || "global"})</span>
                          </div>
                        </Option>
                      ))}
                    </SelectAnt>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* FAQs Section */}
          <div className="col-md-12 mt-4">
            <div className="d-flex align-items-center justify-content-between mb-3" style={{ borderBottom: "2px solid #f3f4f6", paddingBottom: "12px" }}>
              <h5 className="m-0" style={{ fontWeight: 600, color: "#111827", fontSize: "16px" }}>
                Platform FAQs
              </h5>
              <Button
                type="primary"
                size="small"
                htmlType="button"
                onClick={() => {
                  setEditingFaqIndex(null);
                  setFaqFormValues({ question: "", answer: "", priority: 0, isActive: true });
                  setIsFaqModalOpen(true);
                }}
              >
                <i className="fa fa-plus mr-1" /> Add FAQ
              </Button>
            </div>
            
            <Table
              dataSource={values.faqs || []}
              rowKey={(_, idx) => idx}
              pagination={false}
              size="small"
              columns={[
                {
                  title: "#",
                  render: (_, __, idx) => idx + 1,
                  width: 50,
                },
                {
                  title: "Question",
                  dataIndex: "question",
                  render: (q) => q.length > 60 ? `${q.substring(0, 60)}...` : q,
                },
                {
                  title: "Priority",
                  dataIndex: "priority",
                  width: 80,
                  align: "center",
                },
                {
                  title: "Status",
                  dataIndex: "isActive",
                  width: 100,
                  align: "center",
                  render: (active) => (
                    <Tag color={active ? "success" : "default"}>{active ? "Active" : "Inactive"}</Tag>
                  ),
                },
                {
                  title: "Actions",
                  width: 120,
                  align: "center",
                  render: (_, record, idx) => (
                    <Space size="small">
                      <Button
                        type="text"
                        size="small"
                        htmlType="button"
                        onClick={() => {
                          setEditingFaqIndex(idx);
                          setFaqFormValues({
                            question: record.question || "",
                            answer: record.answer || "",
                            priority: record.priority ?? 0,
                            isActive: record.isActive !== false,
                          });
                          setIsFaqModalOpen(true);
                        }}
                      >
                        Edit
                      </Button>
                      <Button
                        type="text"
                        danger
                        size="small"
                        htmlType="button"
                        onClick={() => {
                          const updated = [...values.faqs];
                          updated.splice(idx, 1);
                          setFieldValue("faqs", updated);
                        }}
                      >
                        Delete
                      </Button>
                    </Space>
                  ),
                },
              ]}
            />

            <Modal
              title={editingFaqIndex !== null ? "Edit FAQ" : "Add FAQ"}
              open={isFaqModalOpen}
              zIndex={2000}
              onCancel={() => setIsFaqModalOpen(false)}
              onOk={() => {
                if (!faqFormValues.question || !faqFormValues.answer) {
                  alert("Question and Answer are required.");
                  return;
                }
                const updated = [...(values.faqs || [])];
                if (editingFaqIndex !== null) {
                  updated[editingFaqIndex] = faqFormValues;
                } else {
                  updated.push(faqFormValues);
                }
                setFieldValue("faqs", updated);
                setIsFaqModalOpen(false);
              }}
              okText="Save FAQ"
            >
              <div style={{ display: "flex", flexDirection: "column", gap: "12px", marginTop: "16px" }}>
                <div>
                  <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Question <span className="text-danger">*</span></label>
                  <Input
                    placeholder="e.g. What is the cancellation policy?"
                    value={faqFormValues.question}
                    onChange={(e) => setFaqFormValues({ ...faqFormValues, question: e.target.value })}
                  />
                </div>
                <div>
                  <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Answer <span className="text-danger">*</span></label>
                  <Input.TextArea
                    rows={4}
                    placeholder="e.g. You can cancel anytime from your settings page..."
                    value={faqFormValues.answer}
                    onChange={(e) => setFaqFormValues({ ...faqFormValues, answer: e.target.value })}
                  />
                </div>
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>Priority</label>
                    <Input
                      type="number"
                      min={0}
                      value={faqFormValues.priority}
                      onChange={(e) => setFaqFormValues({ ...faqFormValues, priority: Number(e.target.value) || 0 })}
                    />
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
                    <label style={{ fontWeight: 500, marginBottom: "8px" }}>Active Status</label>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", height: "32px" }}>
                      <Switch
                        checked={faqFormValues.isActive}
                        onChange={(checked) => setFaqFormValues({ ...faqFormValues, isActive: checked })}
                      />
                      <span>{faqFormValues.isActive ? "Active" : "Inactive"}</span>
                    </div>
                  </div>
                </div>
              </div>
            </Modal>
          </div>
        </div>

        <div className="submit-section mb-5 mt-4">
          <button type="submit" className="btn btn-primary submit-btn">
            {isUpdate && platformInfo && platformInfo._id ? "Update Platform" : "Submit Platform"}
          </button>
        </div>
      </Form>
    </FormikProvider>
  );
}

export default FormView;
